import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState, useMemo } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { UserIdChip } from '@/components/ui/UserIdChip';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import { replayTutorial } from '@/components/Tutorial';
import { useAuth } from '@/contexts/AuthContext';
import { useBugReport } from '@/contexts/BugReportContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useProfile } from '@/contexts/ProfileContext';
import { ThemePreference, useTheme } from '@/contexts/ThemeContext';
import { isMuted, setMuted } from '@/lib/sounds';
import { getMyJuryDuties } from '@/lib/disputes';
import type { AppLanguage } from '@/i18n';

const LANGUAGE_OPTIONS: { value: AppLanguage; label: string }[] = [
  { value: 'el', label: 'Ελληνικά' },
  { value: 'en', label: 'English' },
];

export default function SettingsScreen() {
  const { user, signOut } = useAuth();
  const { profile, save } = useProfile();
  const { colors, preference, setPreference } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const { openFeature } = useLocalSearchParams<{ openFeature?: string }>();
  const { open: openBugReport } = useBugReport();

  const [muted, setMutedState] = useState(isMuted());
  const [juryDutyCount, setJuryDutyCount] = useState(0);

  useEffect(() => {
    void getMyJuryDuties().then((list) => setJuryDutyCount(list.filter((d) => !d.already_voted).length)).catch(() => {});
  }, []);

  const THEME_OPTIONS: { value: ThemePreference; label: string; icon: keyof typeof Ionicons.glyphMap }[] = [
    { value: 'light', label: t('settings.theme_light'), icon: 'sunny-outline' },
    { value: 'dark', label: t('settings.theme_dark'), icon: 'moon-outline' },
    { value: 'auto', label: t('settings.theme_auto'), icon: 'phone-portrait-outline' },
  ];

  const styles = useMemo(() => makeStyles(colors), [colors]);

  async function handleLanguageChange(lang: AppLanguage) {
    await setLanguage(lang);
    // Sync στο profile αν είναι συνδεδεμένος
    if (user) {
      try { await save({ language: lang }); } catch { /* non-critical */ }
    }
  }

  useEffect(() => {
    if (openFeature === '1') openBugReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSound() {
    const next = !muted;
    setMutedState(next);
    void setMuted(next);
  }

  return (
    <View style={styles.flex}>
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('settings.title')}</Text>
        <View style={styles.spacer} />
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t('settings.account')}</Text>
        <View style={styles.row}>
          <Text style={styles.rowLabel}>Email</Text>
          <Text style={styles.rowValue} numberOfLines={1}>
            {user?.email}
          </Text>
        </View>
        <Pressable
          style={styles.linkRow}
          onPress={() => router.push('/(app)/profile')}
          accessibilityRole="button"
        >
          <View style={styles.linkLeft}>
            <Ionicons name="person-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.edit_profile')}</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
        </Pressable>
        <Pressable
          style={styles.linkRow}
          onPress={() => router.push('/(app)/identity-verify')}
          accessibilityRole="button"
        >
          <View style={styles.linkLeft}>
            <Ionicons name="shield-checkmark-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.identity_verify')}</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
        </Pressable>
        <Pressable
          style={styles.linkRow}
          onPress={() => router.push('/(app)/payments')}
          accessibilityRole="button"
        >
          <View style={styles.linkLeft}>
            <Ionicons name="receipt-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.payments')}</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
        </Pressable>
        <Pressable
          style={styles.linkRow}
          onPress={() => router.push('/(app)/wallet')}
          accessibilityRole="button"
        >
          <View style={styles.linkLeft}>
            <Ionicons name="create-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.my_pen')}</Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
        </Pressable>
        <Pressable
          style={styles.linkRow}
          onPress={() => router.push('/(app)/jury')}
          accessibilityRole="button"
        >
          <View style={styles.linkLeft}>
            <Ionicons name="people-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.jury_duty')}</Text>
            {juryDutyCount > 0 && (
              <View style={styles.juryBadge}>
                <Text style={styles.juryBadgeText}>{juryDutyCount}</Text>
              </View>
            )}
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
        </Pressable>
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t('settings.preferences')}</Text>

        {/* ── Theme picker ── */}
        <View style={styles.themeRow}>
          <View style={styles.linkLeft}>
            <Ionicons name="contrast-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.appearance')}</Text>
          </View>
          <View style={styles.themeOptions}>
            {THEME_OPTIONS.map((opt) => (
              <Pressable
                key={opt.value}
                style={[styles.themeBtn, preference === opt.value && styles.themeBtnActive]}
                onPress={() => void setPreference(opt.value)}
                accessibilityRole="radio"
                accessibilityState={{ checked: preference === opt.value }}
              >
                <Ionicons
                  name={opt.icon}
                  size={14}
                  color={preference === opt.value ? colors.textOnPrimary : colors.textMuted}
                />
                <Text style={[styles.themeBtnText, preference === opt.value && styles.themeBtnTextActive]}>
                  {opt.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* ── Language picker ── */}
        <View style={styles.themeRow}>
          <View style={styles.linkLeft}>
            <Ionicons name="language-outline" size={18} color={colors.primary} />
            <Text style={styles.linkText}>{t('settings.language')}</Text>
          </View>
          <View style={styles.themeOptions}>
            {LANGUAGE_OPTIONS.map((opt) => (
              <Pressable
                key={opt.value}
                style={[styles.themeBtn, language === opt.value && styles.themeBtnActive]}
                onPress={() => void handleLanguageChange(opt.value)}
                accessibilityRole="radio"
                accessibilityState={{ checked: language === opt.value }}
              >
                <Text style={[styles.themeBtnText, language === opt.value && styles.themeBtnTextActive]}>
                  {opt.label}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>

        {/* ── Sound toggle ── */}
        <Pressable style={styles.soundRow} onPress={toggleSound} accessibilityRole="switch">
          <View style={styles.linkLeft}>
            <Ionicons
              name={muted ? 'volume-mute-outline' : 'volume-high-outline'}
              size={18}
              color={colors.primary}
            />
            <Text style={styles.linkText}>{t('settings.sounds')}</Text>
          </View>
          <View style={[styles.toggle, !muted && styles.toggleOn]}>
            <View style={[styles.knob, !muted && styles.knobOn]} />
          </View>
        </Pressable>
      </View>

      {!!profile && (
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('settings.my_user_id')}</Text>
          <UserIdChip userNumber={profile.user_number} />
          <Text style={styles.idHint}>{t('settings.user_id_hint')}</Text>
        </View>
      )}

      {/* ── Κάρτα Feedback ── */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Feedback</Text>
        <SettingsLink
          icon="bulb-outline"
          label={t('settings.suggest_feature')}
          onPress={() => openBugReport()}
          colors={colors}
          first
        />
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t('settings.legal')}</Text>
        <SettingsLink icon="cloud-download-outline" label={t('settings.my_data')} onPress={() => router.push('/(app)/data')} colors={colors} />
        <SettingsLink icon="document-text-outline" label={t('settings.terms')} onPress={() => router.push({ pathname: '/legal', params: { doc: 'terms' } })} colors={colors} />
        <SettingsLink icon="lock-closed-outline" label={t('settings.privacy')} onPress={() => router.push({ pathname: '/legal', params: { doc: 'privacy' } })} colors={colors} />
        <SettingsLink icon="help-circle-outline" label={t('settings.usage_guide')} onPress={() => void replayTutorial()} colors={colors} />
        <SettingsLink icon="shield-checkmark-outline" label={t('settings.how_it_works')} onPress={() => router.push('/(app)/how-it-works')} colors={colors} />
      </View>

      {profile?.is_admin && (
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('settings.admin')}</Text>
          <SettingsLink
            icon="shield-checkmark-outline"
            label={t('settings.admin_verifications')}
            onPress={() => router.push('/(app)/admin/verifications')}
            colors={colors}
            first
          />
        </View>
      )}

      <Button title={t('settings.sign_out')} variant="secondary" icon="log-out-outline" onPress={signOut} />
    </ScrollView>
    </View>
  );
}

function SettingsLink({
  icon,
  label,
  onPress,
  colors,
  first = false,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  colors: import('@/constants/theme').AppColors;
  first?: boolean;
}) {
  const linkStyles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <Pressable
      style={[linkStyles.linkRow, first && linkStyles.linkRowFirst]}
      onPress={onPress}
      accessibilityRole="button"
    >
      <View style={linkStyles.linkLeft}>
        <Ionicons name={icon} size={18} color={colors.primary} />
        <Text style={linkStyles.linkText}>{label}</Text>
      </View>
      <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
    </Pressable>
  );
}

function makeStyles(colors: import('@/constants/theme').AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: { paddingHorizontal: spacing.lg, gap: spacing.lg },
    topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600', width: 70 },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 70 },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.md,
      ...shadow.card,
    },
    sectionTitle: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    idHint: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 19 },
    themeRow: {
      flexDirection: 'column',
      gap: spacing.sm,
    },
    themeOptions: { flexDirection: 'row', gap: spacing.xs },
    themeBtn: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      paddingVertical: 6,
      paddingHorizontal: spacing.xs,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
    },
    themeBtnActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    themeBtnText: { color: colors.textMuted, fontSize: 12, fontWeight: '600' },
    themeBtnTextActive: { color: colors.textOnPrimary },
    soundRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    toggle: {
      width: 44,
      height: 26,
      borderRadius: 13,
      backgroundColor: colors.borderStrong,
      padding: 3,
      justifyContent: 'center',
    },
    toggleOn: { backgroundColor: colors.primary },
    knob: { width: 20, height: 20, borderRadius: 10, backgroundColor: '#FFFFFF' },
    knobOn: { alignSelf: 'flex-end' },
    row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: spacing.md },
    rowLabel: { color: colors.textMuted, fontSize: fontSize.md },
    rowValue: { color: colors.text, fontSize: fontSize.md, fontWeight: '600', flexShrink: 1 },
    linkRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.md,
    },
    linkRowFirst: { borderTopWidth: 0, paddingTop: 0 },
    linkLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    linkText: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    juryBadge: {
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: colors.danger,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
    },
    juryBadgeText: { color: '#fff', fontSize: 10, fontWeight: '700' },
  });
}
